import { Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import classNames from "classnames";
import { format } from 'date-fns';
import { nanoid } from "nanoid";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { getDataSchema } from '../api/data';
import { AccountContext } from "../context/account";
import { UpdateExploreDataProps } from '../context/explore';
import { MapDataContext } from "../context/map-data";
import { DataSourceModel } from "../core/global";
import { trackEvent } from '../helpers/tracking';
import styles from "../styles/components/explore-data-sources.module.scss";
import tableStyles from "../styles/components/table.module.scss";
import { CopyButton } from "./copy-button";
import { WarningMessage } from './error-message';
import Loader from "./loader";
import { MapPreview } from "./map-preview";
import { ModalTitle } from "./modal-title";
import { ModelSelect } from './models-switch';
import { StyledMarkdown } from "./typeface";


// ApiAccessToken

interface ExploreContentProps {
  model?: DataSourceModel | null
}

export const ExploreContent = ({
  model,
}: ExploreContentProps) => {
  const [loading, setLoading] = useState(false)
  const [vectors, setVectors] = useState<any>(null)
  const [description, setDescription] = useState<any>(null)
  const [timeline, setTimeline] = useState<any>(null)

  const {
    dataSources,
  } = useContext(MapDataContext);

  const [columnWidth, setColumnWidth] = React.useState<number>(0);

  const columnRef = React.useRef<HTMLDivElement | null>(null);

   
  useEffect(() => {
    const execute = async (loadModel?: DataSourceModel | null) => {
      setLoading(true)
      if (!loadModel) {
        setLoading(false)
        return
      }
      const {model, provider, geography, version} = loadModel
      const schemaParams: UpdateExploreDataProps = {
        model, 
        provider, 
        geography,
        version,
      }
      const result = await getDataSchema({
        ...schemaParams
      });
      const { response, error, message } = result;
      const { description, timeline, vectors } = response || {};
      setVectors(vectors)
      setDescription(description)
      setTimeline(timeline)
      setLoading(false)
    }
    if (model) {
      execute(model)
    }
  }, [model])

  useEffect(() => {
    if (columnRef && columnRef.current) {
      const width = columnRef.current.clientWidth
      setColumnWidth(width)
    }
  }, [columnRef?.current])

  const {bounds, shape, title} = useMemo(() => {
    let bounds = null
    let shape = null
    let title = null
    if (dataSources && model) {
      const description = dataSources[model?.key]
      if (description) {
        const {min_lat, min_lon, max_lat, max_lon, name} = description
        title = name
        const hasValidBounds = [min_lat, min_lon, max_lat, max_lon].every((coord: any) => typeof coord === 'number' && !isNaN(coord))
        if (hasValidBounds) {
          bounds = [Number(description.min_lat),Number(description.min_lon),Number(description.max_lat),Number(description.max_lon)]
        }
        const {
          bounds: boundsShape 
        } = description
        shape = boundsShape ? boundsShape : null
      }
      
    }
    return {
      bounds,
      shape,
      title
    }
  }, [dataSources, model])

  const modelFound = useMemo(() => {
    let found = false
    if (model && dataSources) {
      found = !!dataSources[model.key]
    }
    return found
  }, [model, dataSources])

  return (
    <>

    {modelFound ?
      <>
        {model && 
              <div className={styles.twoColumns}>
                <div ref={columnRef}>
                  {(bounds && columnWidth > 0) && 
                    <MapPreview bounds={bounds} overlayPolygon={shape} width={columnWidth} />
                  }
                </div>
                {dataSources && <>
                  
                  <StyledMarkdown>
        {`${dataSources[model?.key].tokens_cost_per_year > 0 ? `**Price** - ${dataSources[model?.key].tokens_cost_per_year*1}GBP/${dataSources[model?.key].tokens_cost_per_year} tokens per location per year  `: `**Price** - This is a free data  `}  
        **Vertical reference** - ${dataSources[model?.key].vertical_reference}  
        **Horizontal resolution** - ${dataSources[model?.key].resolution}  
        **Temporal reference** - ${dataSources[model?.key].temporal_reference}  
        **Temporal range** - ${format(new Date(dataSources[model?.key].start_date), 'dd/MM/yyyy')} - ${format(new Date(dataSources[model?.key].end_date), 'dd/MM/yyyy')}  
        **Temporal resolution** - ${dataSources[model?.key].frequency} minutes  
                `}
                  </StyledMarkdown>
                </>}
              </div>
            }

            {(model && dataSources) && 
              <StyledMarkdown>
                {dataSources[model?.key].description}
              </StyledMarkdown>
            }
          

            {!loading ?
              <>
                <div>
                  {(dataSources && model) && <>
                    <p>Model API variables</p>
                    <table className={classNames(tableStyles.table, tableStyles.tableWrap)}>
                      <thead>
                        <tr>
                          <th>
                            Variable name
                          </th>
                          <th>
                            Key (used for API calls)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Provider</td>
                          <td>{dataSources[model?.key].provider}<CopyButton content={dataSources[model?.key].provider} /></td>
                        </tr>
                        <tr>
                          <td>Model</td>
                          <td>{dataSources[model?.key].model}<CopyButton content={dataSources[model?.key].model} /></td>
                        </tr>
                        <tr>
                          <td>Geography</td>
                          <td>{dataSources[model?.key].geography}<CopyButton content={dataSources[model?.key].geography} /></td>
                        </tr>
                        <tr>
                          <td>Version</td>
                          <td>{`${dataSources[model?.key].version}`}<CopyButton content={dataSources[model?.key].version} /></td>
                        </tr>
                      </tbody>
                    </table>
                  </>
                  }
                  {(vectors && vectors.length > 0) ?
                    <>
                      <p>The following model parameters are available:</p>
                      <br />
                      <>
                        <table className={classNames(tableStyles.table, tableStyles.tableWrap)}>
                          <thead>
                            <tr>
                              <th>
                                Title
                              </th>
                              <th>
                                Units
                              </th>
                              <th>
                                Key (used for API calls)
                              </th>
                              {/* <th className={classNames(tableStyles.centered)} style={{maxWidth: '110px'}}>
                                Directional
                              </th> */}
                            </tr>
                          </thead>
                          <tbody>
                          {vectors.map((vector: any) => {
                            const {name, key, units, directional} = vector
                            return <React.Fragment key={nanoid()}>
                                <tr>
                                  <td>{name}</td>
                                  <td>{units? units : '-'}</td>
                                  <td>{key}<CopyButton content={key} /></td>

                                  {/* <td className={classNames(tableStyles.data, tableStyles.centered)}>
                                    <Chip onClick={() => {}} label={directional? 'Yes' : 'No'} variant="filled"  />
                                  </td> */}
                                </tr>
                              </React.Fragment>
                              
                          })}
                          </tbody>
                        </table>
                      </>

                    </>
                    :
                    <WarningMessage message={`The list of available vectors ${model ? ` for **${model.model}**`: ''} couldn't be loaded.`} />
                  }
                </div>
              </>
              :
              <div style={{padding: "32px 0"}}>
                <Loader small center />
              </div>
            }
      </>
    :
      <WarningMessage message={`The selected model ${model ? `**${model.model}**` : ''} couldn't be loaded.`} /> 
    }
    </>
  )
}

interface TokensModalProps {
  open: boolean
  onClose: () => void
  selectedModel?: DataSourceModel | null
}


export const ExploreDataSourcesModal = ({
  open,
  onClose,
  selectedModel,
}: TokensModalProps) => {
  const [purchaseModel, setPurchaseModel] = useState<DataSourceModel | null>()

  const {
    account,
  } = useContext(AccountContext);

  const {
    dataSources,
    dataSourceModel,
  } = useContext(MapDataContext);

  useEffect(() => {
    if (selectedModel) {
      setPurchaseModel(selectedModel)
    } else {
      setPurchaseModel(dataSourceModel || null)
    }
  }, [dataSourceModel, selectedModel])

  useEffect(() => {
    if (open && account.uid) {
      trackEvent("explore-data-sources-modal", {uid: account.uid})
    }
  }, [open, account])

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <ModalTitle onClose={onClose}>Explore data models</ModalTitle>
      <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', paddingTop: '8px !important' }}
        >
        {purchaseModel && 
          <ModelSelect
            dataSources={dataSources}
            id="explore-model-select"
            value={`${purchaseModel.key}`}
            label={'Model'}
            onChange={(newModelKey: string) => {
              const [provider, model, geography, version = null] = newModelKey.split('-')
              const newModel = {
                key: newModelKey,
                provider,
                model,
                geography,
                version
              } as DataSourceModel
              setPurchaseModel(newModel)
            }}
          />
        }
        <ExploreContent model={purchaseModel} />
      </DialogContent>
      <>
        <DialogActions sx={{justifyContent: 'flex-end', pb: 3, px: 3}}>
          <Button onClick={onClose}>Close</Button>      
        </DialogActions>
      </>
    </Dialog>
  </>
  )
}


